// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Ask Helios conversation state.
 *
 * Global rather than component-local so the thread survives navigation and the
 * panel being closed and reopened — the Ask Helios panel unmounts its body when
 * closed, which used to destroy the conversation.
 *
 * The send action lives here rather than in a hook for the same reason: a stream
 * in flight when the user closes the panel keeps writing into the store and its
 * answer is waiting when they open it again.
 *
 * Deliberately **not** persisted: a hard reload starts a fresh thread. It is also
 * a separate store from `shellStore`, whose `persist` allowlist is ids-only by
 * ADR-0008 Decision 2.
 */

import { create } from 'zustand';
import {
  getConversation,
  streamAskHelios,
  STREAM_STALLED_MESSAGE,
} from '../services/askHeliosService';
import { useShellStore } from './shellStore';
import { useSessionGuardStore } from './sessionGuardStore';
import { hasSelectedSite } from '../hooks/useActiveSite';
import type { AlarmContextSeed, AskHeliosMessage, ConversationMessage } from '../types';

/** Shown in place of the welcome text at render time while no site is selected; never stored. */
export const NO_SITE_WELCOME =
  'Hi, I’m Ask EnergyFluo. Select a site to ask about alarms, live telemetry, SOPs, ' +
  'equipment manuals, or how your sites and equipment connect. I’ll show you where ' +
  'each answer comes from. I’m advisory only, so I won’t change anything on your systems.';

/** Canned reply to a question sent with no site selected; the backend is not called. */
export const NO_SITE_REPLY = 'Please select a site to ask any queries.';

function createWelcomeMessage(): AskHeliosMessage {
  return {
    role: 'assistant',
    content:
      'Hi, I’m Ask EnergyFluo. Ask me about alarms, SOPs, equipment manuals, live ' +
      'telemetry, or how your sites and equipment connect. I’ll show you where each ' +
      'answer comes from. I’m advisory only, so I won’t change anything on your systems.',
    timestamp: new Date(),
    welcome: true,
  };
}

/** Maps a stored turn back onto the shape the message list already renders. */
function toAskHeliosMessage(stored: ConversationMessage): AskHeliosMessage {
  return {
    role: stored.role,
    content: stored.content,
    timestamp: stored.created_at ? new Date(stored.created_at) : new Date(),
    confidence: stored.confidence ?? undefined,
    citations: stored.citations,
    contextChips: stored.context_chips,
    route: stored.route ?? undefined,
    abstained: stored.abstained ?? undefined,
    disclaimer: stored.disclaimer ?? undefined,
  };
}

/** Shown when a request fails or ends without an answer. */
export const UNAVAILABLE_REPLY = 'Sorry — Ask EnergyFluo is temporarily unavailable.';

/** Shown when the stream ended on a 401, which locks the screen for re-sign-in. */
export const SESSION_EXPIRED_REPLY = 'You have been signed out. Sign in again, then Retry.';

/** Shown when the operator stops an answer in flight. */
const STOPPED_REPLY = 'Stopped. Retry to ask again.';

/** State patch that closes a failed turn: an error reply plus a Retry for `question`. */
function failedTurn(state: ChatState, question: string, content: string) {
  return {
    retryableQuestion: question,
    messages: [
      ...state.messages,
      { role: 'assistant' as const, content, timestamp: new Date(), error: true },
    ],
    isThinking: false,
    progressLabel: null,
  };
}

/**
 * The in-flight request, kept outside the store because it is not rendered and
 * a new AbortController per turn would otherwise churn subscribers.
 */
let activeController: AbortController | null = null;

/** Assigns each assistant message a stable client-local id (see `onPersisted`). */
let messageSeq = 0;

/** Hard cap on stored-history pages followed on resume, in case a cursor never advances. */
export const MAX_RESUME_PAGES = 20;

export interface ChatState {
  messages: AskHeliosMessage[];
  inputValue: string;
  isThinking: boolean;
  /** Sanitized progress label while a tool runs; null otherwise. */
  progressLabel: string | null;
  /** Foundry thread id, returned with the first answer and echoed back after. */
  sessionId: string | undefined;
  /** Product conversation id for the active thread; undefined until the first answer. */
  conversationId: string | undefined;
  /** True when the last answer could not be saved — a non-blocking indicator. */
  unsaved: boolean;
  /** True while a stored conversation is being hydrated. */
  isLoadingConversation: boolean;
  /** The last question that failed, so the error turn can offer a retry. */
  retryableQuestion: string | null;
  /** The alarm this thread is scoped to, or null for an ambient thread. */
  threadSeed: AlarmContextSeed | null;
  /**
   * The alarm id this thread was started/resumed for — unlike `threadSeed`,
   * `attachSeed`/`clearThreadSeed` never touch it. The panel's reopen check
   * needs "which alarm is this conversation about" to survive a mid-thread
   * chip detach, which nulls `threadSeed` but does not mean the thread is now
   * about a different (or no) alarm.
   */
  threadOriginSeedId: string | null;
  /**
   * True once `threadSeed` has been included on a request. Gates re-sending
   * independently of `sessionId`/`conversationId` so a seed attached mid-thread
   * (the context chip's "+") still goes out on the very next turn.
   */
  contextSeedSent: boolean;
  /**
   * True once the operator removed an unsent chip in a conversation that
   * already exists, which clears the older seed stored for it (ContextChipsRow
   * hides the remove "x" once a chip is sent). Sent as `context_seed_cleared` on every turn until a new seed
   * is attached, so the BFF stops restoring the stored one (it is idempotent).
   */
  seedCleared: boolean;
  /** Guards against a slow response landing after the thread was reset. */
  requestSeq: number;

  setInputValue: (_value: string) => void;
  /** Sends a question and appends both turns as the answer resolves. */
  ask: (_question: string) => void;
  /** Aborts the in-flight answer and leaves an error turn with a Retry. */
  stop: () => void;
  /** Clears the thread and drops its alarm context (the panel's "+" action). */
  newChat: () => void;
  /** Starts a fresh thread scoped to `seed` (opening Ask Helios from an alarm). */
  resetForSeed: (_seed: AlarmContextSeed) => void;
  /**
   * Scopes the *current* thread to `seed` without resetting it — the context
   * chip's "+" affordance when an alarm page is opened after the panel.
   */
  attachSeed: (_seed: AlarmContextSeed) => void;
  /** Drops the thread's alarm context — the context chip's "x". */
  clearThreadSeed: () => void;
  /** Replaces the thread with a stored conversation (resume / replay). */
  resumeConversation: (_conversationId: string) => Promise<void>;
}

/**
 * The fields a thread reset returns to. A function, not a constant, so each
 * reset gets its own message array and a welcome turn stamped now.
 * `requestSeq` is bumped separately.
 */
function freshThread() {
  return {
    messages: [createWelcomeMessage()],
    isThinking: false,
    progressLabel: null,
    sessionId: undefined,
    conversationId: undefined,
    unsaved: false,
    isLoadingConversation: false,
    retryableQuestion: null,
  };
}

export const useChatStore = create<ChatState>()((set, get) => ({
  ...freshThread(),
  inputValue: '',
  threadSeed: null,
  threadOriginSeedId: null,
  contextSeedSent: false,
  seedCleared: false,
  requestSeq: 0,

  setInputValue: value => set({ inputValue: value }),

  ask: rawQuestion => {
    const question = rawQuestion.trim();
    const {
      isThinking,
      isLoadingConversation,
      messages,
      sessionId,
      conversationId,
      threadSeed,
      contextSeedSent,
      seedCleared,
      requestSeq,
    } = get();
    // `isLoadingConversation` guards the resume window. A resume sets
    // isThinking:false while it hydrates, so the input stayed enabled behind the
    // "Loading conversation…" spinner: sending there bumped requestSeq, the
    // resume discarded its own fetched conversation as stale, and the question
    // started a fresh thread instead — the operator clicked a conversation,
    // asked their follow-up, and the history they were waiting on never arrived.
    if (!question || isThinking || isLoadingConversation) return;

    // Every send path (input box, starter prompt, retry) lands here, so this one
    // guard covers them all. Answer locally — the backend needs a site to scope to.
    // `hasSelectedSite` excludes the fleet sentinel, so `siteId` below is a real site.
    const siteId = useShellStore.getState().selectedSiteId;
    if (!hasSelectedSite(siteId)) {
      const now = new Date();
      set({
        // Supersede any still-open earlier stream (it awaits its persistence
        // trailer after the envelope), so a late error from it cannot append a
        // stale error turn or restore Retry for the old question.
        requestSeq: requestSeq + 1,
        retryableQuestion: null,
        messages: [
          ...messages,
          // Local turns never reach the backend, so they are never saved.
          { role: 'user', content: question, timestamp: now, persisted: false },
          { role: 'assistant', content: NO_SITE_REPLY, timestamp: now, persisted: false },
        ],
      });
      return;
    }

    const seq = requestSeq + 1;
    const controller = new AbortController();
    activeController = controller;

    // Seed the alarm only once per thread — after that it lives in the thread
    // history (and the stored record), so re-sending is redundant. Gating on
    // this flag rather than `sessionId`/`conversationId` lets a seed attached
    // mid-thread (the context chip's "+") go out on the very next turn, even
    // though the thread already has a session/conversation id.
    const seedToSend = contextSeedSent ? undefined : (threadSeed ?? undefined);

    set({
      requestSeq: seq,
      retryableQuestion: null,
      messages: [...messages, { role: 'user', content: question, timestamp: new Date() }],
      isThinking: true,
      progressLabel: null,
    });

    /** True while this request is still the one the UI is waiting on. */
    const current = () => get().requestSeq === seq;

    /** Set once the envelope lands, so a later trailing `persisted` event can find this turn. */
    let assistantMessageId: number | null = null;

    /** True once this request has an envelope or an error turn; `fail` is a no-op after that. */
    let settled = false;
    const fail = (content: string) => {
      // A late error or idle timeout after a good answer must not bury it.
      if (settled) return;
      settled = true;
      set(state => failedTurn(state, question, content));
    };

    void streamAskHelios(
      {
        question,
        session_id: sessionId,
        conversation_id: conversationId,
        current_page: window.location.href,
        // Never the fleet sentinel: the no-site guard above answers locally first.
        dc_id: siteId ?? undefined,
        context_seed: seedToSend,
        // Attaching a seed resets `seedCleared`, so it is never set alongside a seed.
        context_seed_cleared: seedCleared || undefined,
      },
      label => {
        if (current()) set({ progressLabel: label });
      },
      res => {
        if (!current()) return; // superseded by a thread reset
        settled = true;
        const id = ++messageSeq;
        assistantMessageId = id;
        set(state => ({
          sessionId: res.session_id ?? state.sessionId,
          conversationId: res.conversation_id ?? state.conversationId,
          // Persistence is best-effort — the answer is valid either way, so this
          // is a quiet indicator rather than an error turn.
          //
          // Sticky across the thread rather than per-turn. Assigning
          // `res.persisted === false` alone meant a later successful turn cleared
          // the indicator for an earlier failed one, so a thread with an unsaved
          // turn in the middle showed nothing at all — the per-message
          // `persisted` flag was already being recorded and then never read.
          unsaved: state.unsaved || res.persisted === false,
          // Only mark the seed sent once an answer actually lands — marking it
          // eagerly meant a failed or malformed turn would still gate it off,
          // so Retry silently dropped the alarm context on the resend.
          //
          // Guarded on `state.threadSeed === seedToSend` (not just truthiness)
          // because attachSeed can swap in a new seed while this request is
          // still in flight — blindly setting `true` would mark the *new*,
          // never-sent seed as sent and it would never go out.
          contextSeedSent:
            seedToSend && state.threadSeed === seedToSend ? true : state.contextSeedSent,
          messages: [
            ...state.messages,
            {
              id,
              role: 'assistant',
              content: res.text,
              timestamp: new Date(),
              confidence: res.confidence,
              citations: res.citations,
              contextChips: res.context_chips,
              route: res.route,
              abstained: res.abstained,
              disclaimer: res.disclaimer,
              persisted: res.persisted,
            },
          ],
          isThinking: false,
          progressLabel: null,
        }));
      },
      error => {
        // Other error strings are technical; only the stall message is worth showing.
        if (current()) fail(error === STREAM_STALLED_MESSAGE ? error : UNAVAILABLE_REPLY);
      },
      controller.signal,
      // The persistence confirmation arrives after the envelope — the BFF
      // writes to Cosmos in the background — and by the time it does, a
      // follow-up turn may already be in flight: `isThinking` clears as soon
      // as the envelope lands, and a new `ask()` does not abort the prior
      // stream. So this can't target "last assistant message" or gate on
      // `current()` (the request seq the trailer arrived under) — either
      // would drop or misattribute it. Instead it targets the specific
      // message id captured when this turn's envelope landed. A miss (thread
      // reset before the trailer arrives) is a natural no-op.
      persisted => {
        if (assistantMessageId === null) return; // trailer before envelope — backend never does this
        set(state => {
          const idx = state.messages.findIndex(m => m.id === assistantMessageId);
          if (idx === -1) return state;
          const messages = [...state.messages];
          messages[idx] = { ...messages[idx], persisted };
          return { messages, unsaved: state.unsaved || persisted === false };
        });
      }
    ).then(() => {
      // Safety net: a stream that ends without an envelope or error event
      // ([DONE]-only, truncated, or a 401 that never calls onError) would
      // otherwise leave isThinking stuck true and the question unanswered —
      // close it with the same error turn and Retry as a reported error.
      if (current()) {
        fail(useSessionGuardStore.getState().locked ? SESSION_EXPIRED_REPLY : UNAVAILABLE_REPLY);
      }
    });
  },

  stop: () => {
    const { isThinking, messages } = get();
    if (!isThinking) return;
    activeController?.abort();
    activeController = null;
    const question = [...messages].reverse().find(m => m.role === 'user')?.content ?? '';
    // Bumping requestSeq makes every late callback from the aborted stream a no-op.
    set(state => ({
      requestSeq: state.requestSeq + 1,
      ...failedTurn(state, question, STOPPED_REPLY),
    }));
  },

  newChat: () => {
    activeController?.abort();
    activeController = null;
    set(state => ({
      ...freshThread(),
      requestSeq: state.requestSeq + 1,
      threadSeed: null,
      threadOriginSeedId: null,
      contextSeedSent: false,
      seedCleared: false,
    }));
    // The panel header reads the shell's requested seed too — leaving it set
    // would keep showing an alarm context the fresh thread no longer carries.
    useShellStore.getState().clearAskHeliosSeed();
  },

  resetForSeed: seed => {
    activeController?.abort();
    activeController = null;
    set(state => ({
      ...freshThread(),
      requestSeq: state.requestSeq + 1,
      threadSeed: seed,
      threadOriginSeedId: seed.id,
      contextSeedSent: false,
      seedCleared: false,
    }));
  },

  attachSeed: seed => set({ threadSeed: seed, contextSeedSent: false, seedCleared: false }),

  clearThreadSeed: () =>
    set(state => ({
      threadSeed: null,
      // Only worth telling the BFF when an older seed may be stored for this
      // conversation. ContextChipsRow hides the "x" on a sent chip, so in practice
      // this fires when a new unsent chip is removed in an existing conversation.
      seedCleared:
        state.seedCleared ||
        (state.threadSeed !== null &&
          (state.contextSeedSent || state.conversationId !== undefined)),
    })),

  resumeConversation: async id => {
    activeController?.abort();
    activeController = null;
    // Bump the sequence so an in-flight answer from the previous thread cannot
    // append itself to the conversation being resumed.
    const seq = get().requestSeq + 1;
    set({
      requestSeq: seq,
      isLoadingConversation: true,
      isThinking: false,
      progressLabel: null,
      retryableQuestion: null,
      unsaved: false,
      // A resumed conversation is not scoped to whatever alarm the previous
      // thread was — drop it so a pending "+" attach can't leak into it.
      threadSeed: null,
      threadOriginSeedId: null,
      contextSeedSent: false,
      seedCleared: false,
    });

    try {
      const detail = await getConversation(id);
      if (get().requestSeq !== seq) return;

      // The BFF pages stored turns; follow its cursor so a long thread resumes whole.
      const stored = [...detail.messages];
      let cursor = detail.next_cursor;
      for (let pages = 1; cursor != null && pages < MAX_RESUME_PAGES; pages += 1) {
        const page = await getConversation(id, { afterSeq: cursor });
        if (get().requestSeq !== seq) return;
        stored.push(...page.messages);
        // A cursor that does not advance would re-read the same page forever.
        cursor = page.next_cursor != null && page.next_cursor > cursor ? page.next_cursor : null;
      }

      set({
        conversationId: detail.conversation_id,
        // The BFF resolves the provider conversation from the stored record when
        // only conversation_id is sent, so the thread continues without the client
        // holding a provider identifier.
        sessionId: detail.session_id ?? undefined,
        messages: stored.length > 0 ? stored.map(toAskHeliosMessage) : [createWelcomeMessage()],
        // The stored record already carries whatever alarm this conversation was
        // seeded with — show it as attached (and already sent) rather than
        // labeling a resumed alarm thread as ambient.
        threadSeed: detail.context_seed ?? null,
        threadOriginSeedId: detail.context_seed?.id ?? null,
        contextSeedSent: Boolean(detail.context_seed),
      });
    } catch {
      if (get().requestSeq !== seq) return;
      // Drop the previous thread's ids too — leaving them attached would make the
      // next question append to a conversation that is no longer on screen.
      set({
        conversationId: undefined,
        sessionId: undefined,
        messages: [
          createWelcomeMessage(),
          {
            role: 'assistant',
            content: "Sorry — that conversation couldn't be loaded.",
            timestamp: new Date(),
            error: true,
          },
        ],
      });
    } finally {
      if (get().requestSeq === seq) set({ isLoadingConversation: false });
    }
  },
}));
